import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { useBoolean } from 'usehooks-ts';

import CardPicture from 'pages/Location/LocationDetail/ManagerPicture/CardPicture';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import ModalRequestAction from 'components/Modals/ModalRequestAction';

import images from 'assets/images';
import { ReactComponent as AddPictureIcon } from 'assets/images/add-picture.svg';

import { LIMIT_FILE_SIZE, themeColor } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';

import { showSuccess, showToastError } from 'utils';
import {
  normalizeUploadedMediaUrls,
  splitUploadMediaFiles,
} from 'utils/formatUploadImages';
import buildPlaceMediaList from 'utils/buildPlaceMediaList';
import moveItemInArray from 'utils/moveItemInArray';

import styles from './SectionPlacePictureList.module.scss';

const OPTION_UPLOAD_IMAGES = {
  limitValue: 40,
  fileSize: LIMIT_FILE_SIZE.LARGE,
  videoFileSize: LIMIT_FILE_SIZE.BIG,
};

function SectionPlacePictureList(props) {
  const {
    isLoadingAddImageToPublicImages,
    isLoadingUpdatePlaceVideo,
    isLoadingDeletePlaceVideo,
    isLoadingDeleteImageFromPublicImages,
    isLoadingPostPlacePreviewImage,
    isLoadingUploadMultipleMedia,
    isLoadingUploadVideo,

    placeDetail,

    actionAddImageToPublicImages,
    actionUpdatePlaceVideo,
    actionDeletePlaceVideo,
    actionDeleteFromPublicImages,
    actionGetPlaceDetail,
    actionGetRoomNamesSetting,
    actionPostPlacePreviewImage,
    actionUploadMultiMedia,
    actionUploadVideo,
  } = props;

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const inputImageFileRef = React.useRef(null);
  const inputVideoFileRef = React.useRef(null);

  React.useEffect(() => {
    if (placeId) {
      actionGetPlaceDetail({ placeId });
    }
  }, [actionGetPlaceDetail, placeId]);

  const [pictureList, setPictureList] = React.useState([]);
  const [pictureSelected, setPictureSelected] = React.useState([]);
  const {
    value: isOpenModalDeleteImages,
    setFalse: onCloseModalDeleteImages,
    setTrue: onOpenModalDeleteImages,
    toggle: onToggleModalDeleteImages,
  } = useBoolean(false);

  // DELETE IMAGES
  const onCancelModalDeleteImages = React.useCallback(() => {
    onCloseModalDeleteImages();
    setPictureSelected([]);
  }, [onCloseModalDeleteImages]);

  const onConfirmDeleteSelectedImages = React.useCallback(() => {
    if (!pictureSelected.length) {
      showToastError('Phòng không được để trống');
      return;
    }

    const selectedImages = pictureSelected.filter((item) => !item.isVideo);
    const hasSelectedVideos = pictureSelected.some((item) => item.isVideo);

    const callbackDeleteSuccess = () => {
      const newImages = pictureList.filter((item) => (
        !pictureSelected.includes(item)
      ));

      onCloseModalDeleteImages();
      setPictureList(newImages);
      setPictureSelected([]);
    };

    if (selectedImages.length) {
      actionDeleteFromPublicImages({
        placeId,
        images: selectedImages.map((item) => item.image || item),
        callback: !hasSelectedVideos ? callbackDeleteSuccess : undefined,
      });
    }

    if (hasSelectedVideos) {
      actionDeletePlaceVideo({
        placeId,
        callback: callbackDeleteSuccess,
      });
    }
  }, [
    actionDeleteFromPublicImages,
    actionDeletePlaceVideo,
    onCloseModalDeleteImages,
    pictureList,
    pictureSelected,
    placeId,
  ]);

  // Update Place Preview
  const onUpdatePlacePreviewImage = React.useCallback((image) => {
    actionPostPlacePreviewImage({
      placeId,
      image,
    });
  }, [actionPostPlacePreviewImage, placeId]);

  // Drop And Drag
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Select Picture
  const onSelectPicture = React.useCallback((picture, checked) => {
    if (checked) {
      setPictureSelected((prevState) => [...prevState, picture]);
    } else {
      setPictureSelected((prevState) => prevState.filter((item) => item !== picture));
    }
  }, []);

  // Select All Picture
  const onSelectAll = React.useCallback(() => {
    setPictureSelected(pictureList);
  }, [pictureList]);

  // Unselect All Picture
  const onUnSelectAll = React.useCallback(() => {
    setPictureSelected([]);
  }, []);

  // Handle Drag
  const onDebounceChangePositionRoomImage = React.useCallback(
    debounce(() => {
      showSuccess('Thay đổi thứ tự ảnh thành công');
    }, 1000), [],
  );

  const onDragEnd = React.useCallback((e) => {
    const { active, over } = e;
    if (active.id !== over.id) {
      const imagesOnly = pictureList.filter((pic) => !pic.isVideo);
      const videosOnly = pictureList.filter((pic) => pic.isVideo);
      const oldIndex = imagesOnly.findIndex((pic) => pic.id === active.id);
      const newIndex = imagesOnly.findIndex((pic) => pic.id === over.id);

      moveItemInArray(imagesOnly, oldIndex, newIndex);

      setPictureList([...imagesOnly, ...videosOnly]);

      onDebounceChangePositionRoomImage();
    }
  }, [pictureList, onDebounceChangePositionRoomImage]);

  const onClickOpenImageFileInput = React.useCallback(() => {
    if (inputImageFileRef.current) {
      inputImageFileRef.current.click();
    }
  }, []);

  const onClickOpenVideoFileInput = React.useCallback(() => {
    if (inputVideoFileRef.current) {
      inputVideoFileRef.current.click();
    }
  }, []);

  const onChangeAddImages = React.useCallback(async (e) => {
    const { files } = e.target;

    if (!files?.length) {
      return;
    }

    const { images: imageFiles } = splitUploadMediaFiles(
      files,
      OPTION_UPLOAD_IMAGES,
    );
    inputImageFileRef.current.value = '';

    if (!imageFiles.length) {
      return;
    }

    actionUploadMultiMedia({
      images: imageFiles,
      callback: (uploadedImages) => {
        const normalized = normalizeUploadedMediaUrls(uploadedImages);
        if (normalized.length) {
          actionAddImageToPublicImages({
            placeId,
            images: normalized,
          });
        }
      },
    });
  }, [
    actionAddImageToPublicImages,
    actionUploadMultiMedia,
    placeId,
  ]);

  const onChangeAddVideo = React.useCallback(async (e) => {
    const { files } = e.target;

    if (!files?.length) {
      return;
    }

    const { videos: videoFiles } = splitUploadMediaFiles(
      files,
      OPTION_UPLOAD_IMAGES,
    );
    inputVideoFileRef.current.value = '';

    if (!videoFiles.length) {
      return;
    }

    const uploadedVideos = await Promise.all(
      videoFiles.map((item) => new Promise((resolve) => {
        actionUploadVideo({
          file: item.file,
          callback: (uploadedVideo) => {
            resolve(normalizeUploadedMediaUrls(uploadedVideo));
          },
        });
      })),
    ).then((results) => results.flat());

    if (uploadedVideos.length) {
      actionUpdatePlaceVideo({
        placeId,
        video: uploadedVideos.length === 1 ? uploadedVideos[0] : uploadedVideos,
      });
    }
  }, [
    actionUpdatePlaceVideo,
    actionUploadVideo,
    placeId,
  ]);

  React.useEffect(() => {
    setPictureList(buildPlaceMediaList(placeDetail));
  }, [placeDetail]);

  React.useEffect(() => {
    actionGetRoomNamesSetting();
  }, [actionGetRoomNamesSetting]);

  return (
    <>
      <Loading
        loading={
          isLoadingAddImageToPublicImages
          || isLoadingUpdatePlaceVideo
          || isLoadingDeletePlaceVideo
          || isLoadingDeleteImageFromPublicImages
          || isLoadingPostPlacePreviewImage
          || isLoadingUploadMultipleMedia
          || isLoadingUploadVideo
        }
        loaderOnPage
      />
      <Card className={styles.Content}>
        <div className="Section">
          {(() => {
            const imageList = pictureList.filter((pic) => !pic.isVideo);
            const videoList = pictureList.filter((pic) => pic.isVideo);
            return (
              <>
                <h2 className="Section__Title">{`Hình ảnh (${imageList.length})`}</h2>

                <div className="Section__Button_Wrapper">
                  <div className="d-flex">
                    <Button
                      classes="Section__Button_SelectAll"
                      onClick={onSelectAll}
                      color={themeColor.LINK}
                    >
                      Chọn tất cả
                    </Button>
                    <Button
                      classes="Section__Button_Add"
                      disabled={!pictureSelected.length}
                      onClick={onUnSelectAll}
                      color={themeColor.LINK}
                    >
                      Bỏ chọn tất cả
                    </Button>
                    <Button
                      classes="Section__Button_Delete"
                      disabled={!pictureSelected.length}
                      color={themeColor.LINK}
                      onClick={onOpenModalDeleteImages}
                    >
                      Xóa
                    </Button>
                  </div>
                </div>

                <div className="Section__PictureList">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={imageList.map((picture) => picture.id)}
                    >
                      {imageList.map((picture) => (
                        <CardPicture
                          key={picture.id}
                          picture={picture}
                          onCheckboxChange={onSelectPicture}
                          pictureSelected={pictureSelected}
                          isLoading={isLoadingPostPlacePreviewImage}
                          onUpdatePlacePreviewImage={onUpdatePlacePreviewImage}
                          previewImage={placeDetail?.previewImage}
                        />
                      ))}

                      <Button
                        isDefault
                        type="button"
                        onClick={onClickOpenImageFileInput}
                        classes="Section__Upload"
                      >
                        <AddPictureIcon />
                        <span>THÊM ẢNH</span>
                      </Button>

                      <input
                        ref={inputImageFileRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        multiple
                        className="Section__Button_AddPicture__Input"
                        onChange={onChangeAddImages}
                      />
                    </SortableContext>
                  </DndContext>
                </div>

                <hr style={{ margin: '32px 0', borderColor: '#EFF2F7' }} />

                <h2 className="Section__Title">{`Video (${videoList.length})`}</h2>

                <div className="Section__PictureList">
                  {videoList.map((picture) => (
                    <CardPicture
                      key={picture.id}
                      picture={picture}
                      onCheckboxChange={onSelectPicture}
                      pictureSelected={pictureSelected}
                      isLoading={isLoadingPostPlacePreviewImage}
                      onUpdatePlacePreviewImage={onUpdatePlacePreviewImage}
                      previewImage={placeDetail?.previewImage}
                    />
                  ))}

                  <Button
                    isDefault
                    type="button"
                    onClick={onClickOpenVideoFileInput}
                    classes="Section__Upload"
                  >
                    <AddPictureIcon />
                    <span>{videoList.length > 0 ? 'SỬA VIDEO' : 'THÊM VIDEO'}</span>
                  </Button>

                  <input
                    ref={inputVideoFileRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/*"
                    className="Section__Button_AddPicture__Input"
                    onChange={onChangeAddVideo}
                  />
                </div>
              </>
            );
          })()}
        </div>

        <ModalRequestAction
          isOpen={isOpenModalDeleteImages}
          icon={images.modalGarbageCan.default}
          textRequest={'Bạn có chắc chắn muốn xoá \n hình ảnh này không?'}
          onToggle={onToggleModalDeleteImages}
          onCloseModal={onCancelModalDeleteImages}
          onConfirm={onConfirmDeleteSelectedImages}
        />
      </Card>
    </>
  );
}

SectionPlacePictureList.propTypes = {
  isLoadingAddImageToPublicImages: PropTypes.bool,
  isLoadingUpdatePlaceVideo: PropTypes.bool,
  isLoadingDeletePlaceVideo: PropTypes.bool,
  isLoadingDeleteImageFromPublicImages: PropTypes.bool,
  isLoadingPostPlacePreviewImage: PropTypes.bool,
  isLoadingUploadMultipleMedia: PropTypes.bool,
  isLoadingUploadVideo: PropTypes.bool,

  placeDetail: PropTypes.instanceOf(Object),

  actionAddImageToPublicImages: PropTypes.func,
  actionUpdatePlaceVideo: PropTypes.func,
  actionDeletePlaceVideo: PropTypes.func,
  actionDeleteFromPublicImages: PropTypes.func,
  actionGetPlaceDetail: PropTypes.func,
  actionGetRoomNamesSetting: PropTypes.func,
  actionPostPlacePreviewImage: PropTypes.func,
  actionUploadMultiMedia: PropTypes.func,
  actionUploadVideo: PropTypes.func,
};

SectionPlacePictureList.defaultProps = {
  isLoadingAddImageToPublicImages: false,
  isLoadingUpdatePlaceVideo: false,
  isLoadingDeletePlaceVideo: false,
  isLoadingDeleteImageFromPublicImages: false,
  isLoadingPostPlacePreviewImage: false,
  isLoadingUploadMultipleMedia: false,
  isLoadingUploadVideo: false,

  placeDetail: INITIAL_VALUE.OBJECT,

  actionAddImageToPublicImages: INITIAL_VALUE.FUNCTION,
  actionUpdatePlaceVideo: INITIAL_VALUE.FUNCTION,
  actionDeletePlaceVideo: INITIAL_VALUE.FUNCTION,
  actionDeleteFromPublicImages: INITIAL_VALUE.FUNCTION,
  actionGetPlaceDetail: INITIAL_VALUE.FUNCTION,
  actionGetRoomNamesSetting: INITIAL_VALUE.FUNCTION,
  actionPostPlacePreviewImage: INITIAL_VALUE.FUNCTION,
  actionUploadMultiMedia: INITIAL_VALUE.FUNCTION,
  actionUploadVideo: INITIAL_VALUE.FUNCTION,
};

export default React.memo(SectionPlacePictureList);
